/**
 * **曲线点 → 绘图数据**（净资产曲线 J2 · 批 121 / K4 抽出）
 *
 * ## 为什么从 `NetWorthChart.tsx` 里抽出来
 *
 * 这段映射里有两条**只有直测才靠得住**的规则：
 *
 * | 规则 | 退化后的症状 |
 * |---|---|
 * | 🔴 **只映射 `origin === 'live'`**（回填点不进绘图数据） | 回填点混进曲线 ⇒ 出现**不存在的"历史跳升"** |
 * | 🔴 **每个点用它自己的 `baseCurrency` 生成文本** | 切过本币后，USD 段的 `$1,000` 被印成 `¥1,000`（`DR-036` D5 明禁） |
 *
 * 而端侧测试基座是**静态渲染**：Tooltip 的文本压根不出现在 HTML 里（Recharts 的 `Tooltip`
 * 只在交互时渲染）⇒ 留在组件里就只能靠"肉眼看图"。抽成纯函数后两条规则都能直测。
 *
 * ## 🔴 第二条为什么不是"用响应顶层的 `baseCurrency`"
 *
 * `NetWorthCurveResponse.baseCurrency` 是**当前**本币；而 `points[].baseCurrency` 是
 * **每个点写入当天**的本币（`net_worth_snapshots.base_currency`）。账本切过币之后两者**不同** ——
 * 用顶层那个统一渲染，等于把历史点的外币金额伪装成本币，这正是 `DR-020` §5 D2 与
 * `DR-036` D5 同时禁止的事。
 */

import { centsToYuanText, formatMoneyText, type Cents, type NetWorthSnapshotView } from '@jiaying/shared';

/** 图表里的一个点（金额已转成展示文本） */
export interface ChartPoint {
  date: string;
  /** 原始分值（Tooltip 用，**不参与坐标计算**以免浮点） */
  cents: number;
  /**
   * 🔴 该点**自己**的币种（快照写入当日的账本本币），不是响应顶层那个"当前本币"。
   *
   * ⚠️ 字段名刻意与契约 `NetWorthSnapshotView.baseCurrency` **保持一致**（不叫 `currency`）——
   * `splitCurrencySegments()` 按 `baseCurrency` 判分段，名字一致才能把绘图数据**直接**喂给它。
   * 曾经叫 `currency` 时，分段函数读到的是 `undefined` ⇒ 所有点被判成同一币种 ⇒
   * **变更点恒为 0**（图上一条标注都不出现，且不报错）。这个坑由 `NetWorthChart.spec.tsx` 钉住。
   */
  baseCurrency: string;
  /** 展示文本（带币种符号与千分位）—— Tooltip 用 */
  text: string;
  /** Y 轴数值 —— 🔴 用 `Number(裸文本)` 而非 `cents / 100`：文本已是精确的两位小数 */
  value: number;
}

/**
 * 快照点 → 绘图数据。
 *
 * 🔴 **不改顺序、不补点、不平推、不折算** —— 这四件事一旦发生，曲线就不再是"真实快照"了
 * （`DR-012` 拍板原话「过去的不能预测将来的曲线」）。
 *
 * @param points 契约下发的点（**升序**，服务端已保证）
 * @returns 仅含 `live` 点的绘图数据（顺序与入参一致）
 */
export function toChartPoints(points: readonly NetWorthSnapshotView[]): ChartPoint[] {
  return points
    .filter((p) => p.origin === 'live')
    .map((p) => {
      const cents = p.netWorth as Cents;
      // `value` 必须取**纯数字文本**：带 `¥` 与千分位会让 `Number()` 得到 `NaN`
      const bare = centsToYuanText(cents);
      return {
        date: p.date,
        cents: p.netWorth,
        baseCurrency: p.baseCurrency,
        // 🔴 逐点取自己的币种（见文件头说明）
        text: formatMoneyText(cents, p.baseCurrency),
        value: Number(bare),
      };
    });
}
